import { Accordion, AccordionItem, Button, Selection } from "@heroui/react";
import { Icon } from "@iconify/react";
import { motion } from "framer-motion";
import React from "react";
import Navbar from "../landing/modern/Navbar";
import ModernFooter from "../landing/modern/ModernFooter";
import SEOHead from "./SEOHead";
import { buildAppLink } from "../../utils/landingRedirect";

interface HowItWorksStep {
  title: string;
  description: string;
}

interface Benefit {
  title: string;
  description: string;
  icon: string;
}

interface FAQ {
  question: string;
  answer: string;
}

interface RelatedTool {
  name: string;
  description: string;
  href: string;
  icon: string;
  iconColor: string;
}

interface ToolPageLayoutProps {
  // SEO
  title: string;
  metaDescription: string;
  keywords?: string;
  canonicalUrl?: string;
  structuredData?: object;

  // Hero Section
  heroTitle: string;
  heroSubtitle: string;
  heroIcon: string;
  heroIconColor?: string;
  heroIconBg?: string;

  // Tool Input Area (children)
  children: React.ReactNode;

  // How It Works Section
  howItWorksSteps: HowItWorksStep[];

  // Benefits Section
  benefitsTitle?: string;
  benefits: Benefit[];

  // FAQ Section
  faqs: FAQ[];

  // Related Tools
  relatedTools: RelatedTool[];
}

/**
 * Reusable layout component for all free AI tool pages.
 * Provides consistent structure, styling, and SEO optimization.
 */
const ToolPageLayout: React.FC<ToolPageLayoutProps> = ({
  // SEO props
  title,
  metaDescription,
  keywords,
  canonicalUrl,
  structuredData,

  // Hero props
  heroTitle,
  heroSubtitle,
  heroIcon,
  heroIconColor = "text-cornflowerblue",
  heroIconBg = "bg-blue-50",

  // Content
  children,

  // Sections
  howItWorksSteps,
  benefitsTitle = "Why Choose YouWo AI?",
  benefits,
  faqs,
  relatedTools,
}) => {
  const [faqSelectedKeys, setFaqSelectedKeys] = React.useState<Selection>(new Set(["0"]));

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title={title}
        description={metaDescription}
        keywords={keywords}
        canonicalUrl={canonicalUrl}
        structuredData={structuredData}
      />

      <Navbar />

      {/* Hero Section */}
      <section className="pt-28 pb-12 md:pt-36 md:pb-16 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <motion.div
            className="text-center max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="flex justify-center mb-6">
              <div className={`w-16 h-16 rounded-2xl flex items-center justify-center ${heroIconBg || 'bg-blue-50'}`}>
                <Icon icon={heroIcon} className={`text-3xl ${heroIconColor}`} />
              </div>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              {heroTitle}
            </h1>
            <p className="text-lg md:text-xl text-gray-600 leading-relaxed">
              {heroSubtitle}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Tool Input Area */}
      <section className="py-8 md:py-12">
        <div className="container mx-auto px-4">
          <motion.div
            className="max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            {children}
          </motion.div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-16 md:py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              How It Works
            </h2>
            <p className="text-gray-600 max-w-xl mx-auto">
              Get your results in just 3 simple steps
            </p>
          </motion.div>

          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-3 gap-8">
              {howItWorksSteps.map((step, index) => (
                <motion.div
                  key={index}
                  className="relative"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  {/* Connector line */}
                  {index < howItWorksSteps.length - 1 && (
                    <div className="hidden md:block absolute top-8 left-1/2 w-full h-0.5 bg-gradient-to-r from-cornflowerblue to-blue-300" />
                  )}

                  <div className="relative bg-white rounded-2xl p-6 shadow-lg border border-gray-100 text-center">
                    {/* Step number */}
                    <div className="w-12 h-12 rounded-full bg-cornflowerblue text-white font-bold text-xl flex items-center justify-center mx-auto mb-4">
                      {index + 1}
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      {step.title}
                    </h3>
                    <p className="text-gray-600 text-sm">
                      {step.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 md:py-20">
        <div className="container mx-auto px-4">
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {benefitsTitle}
            </h2>
          </motion.div>

          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {benefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  className="bg-white rounded-2xl p-6 border border-gray-100 hover:border-cornflowerblue/30 hover:shadow-lg transition-all duration-300"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                >
                  <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center mb-4">
                    <Icon icon={benefit.icon} className="text-cornflowerblue text-2xl" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {benefit.title}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {benefit.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-12 md:py-16 bg-gradient-to-r from-cornflowerblue to-blue-600">
        <div className="container mx-auto px-4">
          <motion.div
            className="text-center max-w-2xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              Ready to Boost Your Productivity?
            </h2>
            <p className="text-blue-100 mb-8">
              Join thousands of students and professionals who save hours every week with YouWo AI.
            </p>
            <Button
              as="a"
              href={buildAppLink()}
              target="_blank"
              rel="noopener noreferrer"
              size="lg"
              className="bg-white text-cornflowerblue font-semibold px-8 py-6 hover:bg-gray-50 no-underline"
            >
              Try YouWo AI for Free
              <Icon icon="lucide:arrow-right" className="ml-2" />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Related Tools Section */}
      <section className="py-16 md:py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Explore More AI Tools
            </h2>
            <p className="text-gray-600">
              Powered by the YouWo AI AI Engine
            </p>
          </motion.div>

          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-2 gap-6">
              {relatedTools.map((tool, index) => (
                <motion.a
                  key={index}
                  href={tool.href}
                  className="group bg-white rounded-2xl p-6 border border-gray-100 hover:border-cornflowerblue/30 hover:shadow-lg transition-all duration-300 no-underline"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${tool.iconColor} bg-opacity-10`}
                         style={{ backgroundColor: `currentColor`, opacity: 0.1 }}>
                      <Icon icon={tool.icon} className={`text-2xl ${tool.iconColor}`} />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 mb-1 group-hover:text-cornflowerblue transition-colors">
                        {tool.name}
                      </h3>
                      <p className="text-gray-600 text-sm">
                        {tool.description}
                      </p>
                      <span className="inline-flex items-center gap-1 text-cornflowerblue text-sm font-medium mt-3 group-hover:gap-2 transition-all">
                        Explore
                        <Icon icon="lucide:arrow-right" />
                      </span>
                    </div>
                  </div>
                </motion.a>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 md:py-20">
        <div className="container mx-auto px-4">
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
          </motion.div>

          <motion.div
            className="max-w-3xl mx-auto bg-gray-50 rounded-3xl p-6 md:p-8"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Accordion
              selectedKeys={faqSelectedKeys}
              onSelectionChange={setFaqSelectedKeys}
              variant="light"
              itemClasses={{
                base: "py-0 w-full",
                title: "font-normal text-medium",
                trigger: "px-2 py-0 data-[hover=true]:bg-default-100 rounded-lg h-14 flex items-center",
                indicator: "text-medium",
                content: "text-small px-2",
              }}
              showDivider={false}
            >
              {faqs.map((faq, index) => {
                const isOpen = Array.from(faqSelectedKeys).includes(String(index));
                return (
                  <AccordionItem
                    key={String(index)}
                    aria-label={faq.question}
                    textValue={faq.question}
                    hideIndicator
                    title={
                      <div className="flex items-center gap-4 w-full">
                        <div className={`transition-transform duration-300 ${isOpen ? "rotate-45" : "rotate-0"}`}>
                          <Icon icon="lucide:plus" className="text-gray-500 text-xl" />
                        </div>
                        <span className={`text-base md:text-lg font-semibold ${isOpen ? "text-gray-900" : "text-gray-700"}`}>
                          {faq.question}
                        </span>
                      </div>
                    }
                  >
                    <div className="pl-9 pb-4 text-gray-600 leading-relaxed text-base">
                      {faq.answer}
                    </div>
                  </AccordionItem>
                );
              })}
            </Accordion>
          </motion.div>
        </div>
      </section>

      <ModernFooter />
    </div>
  );
};

export default ToolPageLayout;
